import React from "react";

export const inputClass = (hasError) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-500 focus:border-red-500 focus:ring-red-200"
      : "border-gray-300 focus:border-brand-navy focus:ring-brand-yellow/60"
  }`;

// Label + control + a fixed-height error line, so messages appearing or
// disappearing never shift the rest of the form.
function FormField({ id, label, required, error, hint, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-800 mb-1.5">
        {label}
        {required && (
          <span className="text-red-600 ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      <p
        id={`${id}-error`}
        className={`mt-1 min-h-[1.25rem] text-xs sm:text-sm ${error ? "text-red-600" : "text-gray-500"}`}
      >
        {error || hint || ""}
      </p>
    </div>
  );
}

// Visually hidden trap field; bots fill it, people never see it.
export function Honeypot({ value, onChange }) {
  return (
    <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
      <label htmlFor="website">Leave this field empty</label>
      <input
        id="website"
        name="website"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

export function FormStatus({ status, message }) {
  const styles = {
    success: "bg-green-50 text-green-800 border-green-200",
    error: "bg-red-50 text-red-800 border-red-200",
    submitting: "bg-gray-50 text-gray-700 border-gray-200",
  };
  return (
    <div role="status" aria-live="polite" className="min-h-[4.25rem] pb-3">
      {message && (
        <p className={`rounded-xl border px-4 py-3 text-sm font-medium ${styles[status] || ""}`}>
          {message}
        </p>
      )}
    </div>
  );
}

export function SubmitButton({ submitting, children, submittingLabel }) {
  return (
    <button
      type="submit"
      disabled={submitting}
      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy px-8 py-3.5 font-bold text-white shadow-lg transition-all duration-300 hover:bg-brand-navy-light hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70"
    >
      {submitting && (
        <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
          <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      )}
      {submitting ? submittingLabel : children}
    </button>
  );
}

export default FormField;
